import axios from 'axios';
import { isNil, isEmpty } from 'lodash';
import { DESIGN_API_URL } from '../lib/environment.js';

import { newAbortSignalFunc } from './modules/api.js';
import templates from './templates.js';

// Email blocks are templates with a variant of 'emailBlock'. They are reusable
// pieces of email content (headers, footers, disclaimers, signatures, …) that the
// main email editor can pull into a template. The portable content stored for a
// block is a single Wrapper subtree (no root Page) — see template.jsx for the
// wrap/unwrap logic used when loading/saving in the editor.
const VARIANT = 'emailBlock';

const emailBlocks = () => {
	let emailBlocksCancelToken = axios.CancelToken.source();
	let timeoutAmount = 180000; // Aborts request after 3 minutes

	return {
		cancel: () => {
			emailBlocksCancelToken.cancel('Operation canceled by the user');
			emailBlocksCancelToken = axios.CancelToken.source();
		},
		// Blocks ARE templates — same collection, same /templates endpoint — so
		// mutations go through the templates service rather than a second copy of
		// the same axios call. That service owns the payload rules the API enforces:
		// source.type is create-only, source.containsVideo/dtUpd are server-owned,
		// and a PATCH must send source in dot notation. Hand-rolling the request
		// here is what got every block save rejected with offender "source".
		create: (data) => templates.create({ ...data, variant: VARIANT }),

		read: (blockId = null, queryParams = {}, limit = 24, offset = 0) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				// Always constrain reads to the emailBlock variant. Callers may pass
				// extra filters (status, owner, category, isHidden, name) via queryParams.
				const blockQueryParams = {
					variant: VARIANT,
					...queryParams,
				};

				const params = new URLSearchParams({
					q: JSON.stringify(blockQueryParams),
					limit,
					offset,
					sort: JSON.stringify(
						blockQueryParams?.name ? { name: 'asc' } : { dtUpd: 'desc' },
					),
				});

				if (isNil(blockId) || isEmpty(blockId)) {
					axios({
						url: `${DESIGN_API_URL}/templates/?${params.toString()}`,
						method: 'GET',
						cache: 'no-cache',
						signal: newAbortSignalFunc(timeoutAmount),
						cancelToken: emailBlocksCancelToken.token,
					})
						.then((resp) => {
							clearTimeout(timeout);
							if (resp.data.status === 0) {
								resolve(resp.data);
							} else {
								reject(resp.data);
							}
						})
						.catch((err) => {
							clearTimeout(timeout);
							reject(err);
						});
				} else {
					axios({
						url: `${DESIGN_API_URL}/templates/${blockId}`,
						method: 'GET',
						cache: 'no-cache',
						signal: newAbortSignalFunc(timeoutAmount),
						cancelToken: emailBlocksCancelToken.token,
					})
						.then((resp) => {
							clearTimeout(timeout);
							if (resp.data.status === 0) {
								// Verify it's an emailBlock variant
								if (resp.data.result.variant !== VARIANT) {
									reject(
										new Error('Requested template is not an email block'),
									);
									return;
								}
								resolve(resp.data.result);
							} else {
								reject(resp.data);
							}
						})
						.catch((err) => {
							clearTimeout(timeout);
							reject(err);
						});
				}
			});
		},

		update: (blockId, blockData) =>
			templates.update(blockId, { ...blockData, variant: VARIANT }),

		delete: (blockId) => templates.delete(blockId),

		// Custom read for handling pagination with next URLs
		customRead: (url) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: url,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
					cancelToken: emailBlocksCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		setVisibility: (blockId, visibility, userKey) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				if (!userKey) {
					reject('User key is required');
					return;
				}

				axios({
					url: `${DESIGN_API_URL}/templates/${blockId}`,
					method: 'PATCH',
					data: {
						[`prefsOf.customers.${userKey}.hide`]: visibility,
					},
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},
	};
};

export default emailBlocks();
